/* eslint-disable max-len */
import { FC, useEffect, useState } from 'react';
import { Button, Checkbox, Empty, Form, Input, Modal, Row, Select, Typography } from 'antd';
import apiRoutes from 'config/apiRoute';
import apiRequests from 'utils/api';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import appRoutes from 'config/appRoutes';
import asyncErrorHandler from 'utils/asyncErrorHandler';
import SelectSearchFrontend from 'components/SelectSearchFrontend';
import { getCustomerSelect } from 'modules/customer/store/customerSelect-slice';
import { parseTimeToMinutes } from 'utils/string.utils';
import useRequestSource from 'utils/useRequestSource';

interface AddProjectModalProps {
  visible: boolean;
  onClose: any;
}

const formatHours = (totalMinutes: number) => {
  if (!totalMinutes) return null;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}:${minutes.toString().padStart(2, '0')}`;
};

const { Title } = Typography;
const AddProjectModal: FC<AddProjectModalProps> = ({ visible, onClose }) => {
  useRequestSource('AddProjectModal', visible);

  const [form] = Form.useForm();
  const [isFormValid, setIsFormValid] = useState(false);
  const [hasBillingEmail, setHasBillingEmail] = useState<boolean>(false);

  const [state, setState] = useState({ saving: false });
  const { saving } = state;

  const [managers, setManagers] = useState([]);
  const [plans, setPlans] = useState<any[]>([]);

  const history = useHistory();

  const createProject = async () => {
    try {
      const values = await form.validateFields();

      if (values.billing_email) {
        [values.business_name] = values.customer_id;
        delete values.customer_id;
      } else {
        [values.customer_id] = values.customer_id;
        delete values.business_name;
      }

      if (typeof values.refill === 'string' && values.refill.includes(':')) {
        values.refill = parseTimeToMinutes(values.refill);
      }

      const payload = {
        ...values,
        title: plans.find((p) => p.name === values.plan)?.display_name,
        status: 'active',
        start_date: moment().format('YYYY-MM-DD'),
      };

      const res = await apiRequests.post(apiRoutes.PROJECTS, payload);

      history.push(`${appRoutes.PROJECTS}/${res?.data?.data?.uuid}`);
      if (onClose) {
        onClose();
      }

      setState((prevState) => ({ ...prevState, saving: false }));
    } catch (error) {
      asyncErrorHandler(error);
      setState((prevState) => ({ ...prevState, saving: false }));
    }
  };

  const fetchManagers = async () => {
    const response = await apiRequests.get(apiRoutes.PROJECT_USERS, {
      'page[size]': 999,
      'filters[auth_users.status][]': 'active',
      'filters[billing_contacts.status][]': 'active',
      'filters[type][]': 'regular_user',
    });

    if (response.data.data) {
      setManagers(response.data.data);
    }
  };

  const fetchPlans = async () => {
    const response = await apiRequests.get(apiRoutes.PLANS);

    if (response.data.data) {
      setPlans(response.data.data);
    }
  };

  const checkFormValidity = (fields: any[]) => {
    if (!fields || fields.length === 0) return false;

    return fields.every((field) => {
      // The billable checkbox defaults to true and may be unchecked on purpose, so it
      // must never gate form validity.
      if (Array.isArray(field.name) && field.name[0] === 'billable') return true;

      const val = field.value;

      if (val === undefined || val === null) return false;

      if (Array.isArray(val)) {
        return val.length > 0;
      }

      if (typeof val === 'string') {
        return val.trim().length > 0;
      }

      if (hasBillingEmail) {
        const billing = form.getFieldValue('billing_email');
        return !!billing;
      }
      return !!val;
    });
  };

  const handleHasBillingEmail = (value: boolean): boolean => {
    setHasBillingEmail(value);
    return value;
  };

  useEffect(() => {
    if (visible) {
      fetchManagers();
      fetchPlans();
      setIsFormValid(false);
    }
  }, [visible]);

  return (
    <Modal
      confirmLoading={saving}
      maskClosable={false}
      footer={null}
      onCancel={onClose}
      title={
        <Row justify="space-between">
          <Title className="mb-0 primary" level={3}>
            Create a new plan
          </Title>
        </Row>
      }
      visible={visible}
      className="modal-no-header-padding"
      bodyStyle={{ padding: '24px 0' }}
    >
      <Form
        colon={false}
        form={form}
        labelCol={{ span: 6 }}
        labelAlign="left"
        initialValues={{
          type: 'is_retainer',
          billable: true,
        }}
        requiredMark={false}
        validateTrigger="onSubmit"
        onFieldsChange={(changedFields, allFields) => {
          setIsFormValid(checkFormValidity(allFields));
        }}
      >
        <SelectSearchFrontend
          autoFocus
          allowClear
          label="Client name:"
          name="customer_id"
          rules={[
            {
              required: true,
              message: 'This field is required',
            },
          ]}
          placeholder="Select a customer"
          searchKeys={['phone', 'business_name', 'status', 'email', 'website', 'abbreviation']}
          optionLabel="business_name"
          storeKeySelector="customerSelect"
          storeAction={() => getCustomerSelect({ has_active_project: false })}
          formMode
          form={form}
        />

        <Form.Item
          noStyle
          shouldUpdate={(prev, curr) => prev.business_name !== curr.business_name || prev.customer_id !== curr.customer_id}
        >
          {({ getFieldValue }) => {
            const customerId = getFieldValue('customer_id');
            const businessName = getFieldValue('business_name');

            const shouldShowBillingEmail = handleHasBillingEmail(
              customerId && customerId.length > 0 && typeof businessName !== 'string',
            );

            return shouldShowBillingEmail ? (
              <Form.Item
                rules={[
                  { required: true, message: 'This field is required' },
                  { type: 'email', message: 'The input is not valid E-mail!' },
                ]}
                name="billing_email"
                label="Billing email:"
              >
                <Input placeholder="accounting@email.com" />
              </Form.Item>
            ) : null;
          }}
        </Form.Item>

        <Form.Item
          label="Type:"
          name="plan"
          rules={[
            {
              required: true,
              message: 'This field is required',
            },
          ]}
        >
          <Select
            placeholder="Select a plan type"
            showSearch
            optionFilterProp="children"
            notFoundContent={<Empty description="No plans found" />}
            onChange={(value) => {
              const plan = plans.find((p) => p.name === value);
              if (plan) {
                form.setFieldsValue({ refill: formatHours(plan.refill) });
              }
            }}
          >
            {plans.map((plan) => (
              <Select.Option key={plan.name} value={plan.name}>
                {plan.display_name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item noStyle shouldUpdate={(prev, curr) => prev.plan !== curr.plan}>
          {({ getFieldValue }) => {
            const selectedPlan = getFieldValue('plan');
            const plan = plans.find((p) => p.name === selectedPlan);
            const editable = plan?.refill_editable ?? false;

            return (
              <Form.Item label="Reload amount:" name="refill" rules={[{ required: true, message: 'This field is required' }]}>
                <Input
                  placeholder="00:00"
                  onChange={(event) => {
                    const value = parseFloat(event.target.value.replace(/\D/g, '')) / 100;
                    const newValue = value.toFixed(2).replace('.', ':');
                    form.setFieldsValue({ refill: newValue === '0:00' ? null : newValue });
                  }}
                  disabled={!selectedPlan || !editable}
                />
              </Form.Item>
            );
          }}
        </Form.Item>

        <Form.Item
          label="Manager:"
          name="project_manager_id"
          rules={[
            {
              required: true,
              message: 'This field is required',
            },
          ]}
        >
          <Select placeholder="Select a manager" showSearch optionFilterProp="children" notFoundContent={<Empty />}>
            {managers.map((manager: any) => (
              <Select.Option key={manager.uuid} value={manager.uuid}>
                {manager.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Billable:" name="billable" valuePropName="checked" className="mb-s">
          <Checkbox />
        </Form.Item>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span className="danger" style={{ fontSize: 12 }}>
            * All fields are required
          </span>
          <Button type="primary" loading={saving} onClick={createProject} disabled={!isFormValid}>
            Save
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default AddProjectModal;
